import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Layout Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Public Pages
import Home from './pages/Home';
import Apartments from './pages/Apartments';
import ApartmentDetail from './pages/ApartmentDetail';
import Locations from './pages/Locations';
import LocationDetail from './pages/LocationDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Terms from './pages/Terms';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Booking Pages
import Booking from './pages/Booking';
import BookingConfirmation from './pages/BookingConfirmation';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminApartments from './pages/admin/AdminApartments';
import AdminApartmentForm from './pages/admin/AdminApartmentForm';
import AdminBookings from './pages/admin/AdminBookings';
import AdminReviews from './pages/admin/AdminReviews';
import AdminUsers from './pages/admin/AdminUsers';
import AdminLocations from './pages/admin/AdminLocations';
import AdminSettings from './pages/admin/AdminSettings';

// Protected Route for Admins
const AdminRoute = ({ children }) => {
  const { isAdminAuthenticated } = useAuth();
  if (!isAdminAuthenticated()) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

// Layout wrapper for public pages
const PublicLayout = ({ children }) => (
  <div className="d-flex flex-column min-vh-100">
    <Navbar />
    <main className="flex-grow-1">{children}</main>
    <Footer />
  </div>
);

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/apartments" element={<PublicLayout><Apartments /></PublicLayout>} />
      <Route path="/apartments/:slug" element={<PublicLayout><ApartmentDetail /></PublicLayout>} />
      <Route path="/locations" element={<PublicLayout><Locations /></PublicLayout>} />
      <Route path="/locations/:slug" element={<PublicLayout><LocationDetail /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />
      <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />

      {/* Booking Routes */}
      <Route path="/booking/:apartmentId" element={<PublicLayout><Booking /></PublicLayout>} />
      <Route path="/booking/confirmation/:bookingId" element={<PublicLayout><BookingConfirmation /></PublicLayout>} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="apartments" element={<AdminApartments />} />
        <Route path="apartments/new" element={<AdminApartmentForm />} />
        <Route path="apartments/:id/edit" element={<AdminApartmentForm />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="locations" element={<AdminLocations />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* 404 - Catch all */}
      <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
    </Routes>
  );
}

// Simple 404 component
const NotFound = () => (
  <div className="container py-5 text-center">
    <h1 style={{ fontSize: '6rem', fontWeight: '700', color: '#1a3a5c' }}>404</h1>
    <h2 className="mb-4">Page Not Found</h2>
    <p className="text-secondary mb-4">The page you're looking for doesn't exist or has been moved.</p>
    <a href="/" className="btn btn-primary">Go Home</a>
  </div>
);

export default App;
